import React, { useState, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { useData } from '@/components/DataContext';
import { parseProfileFile, importProfile, type ProfileData, type ImportProgress } from '@/utils/profile';
import { Upload, FileText, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';

interface ProfileImportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export default function ProfileImportDialog({ open, onOpenChange }: ProfileImportDialogProps) {
  const { toast } = useToast();
  const { refreshData } = useData();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [profileData, setProfileData] = useState<ProfileData | null>(null);
  const [isLoadingFile, setIsLoadingFile] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [progress, setProgress] = useState<ImportProgress | null>(null);
  const [error, setError] = useState<string | null>(null);

  const resetState = () => {
    setSelectedFile(null);
    setProfileData(null);
    setIsLoadingFile(false);
    setIsImporting(false);
    setProgress(null);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDialogChange = (isOpen: boolean) => {
    if (isImporting) return; // prevent closing while importing
    if (!isOpen) {
      resetState();
    }
    onOpenChange(isOpen);
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setError(null);
    setSelectedFile(file);
    setIsLoadingFile(true);

    try {
      const parsed = await parseProfileFile(file);
      setProfileData(parsed);
    } catch (err: any) {
      setError(err.message || 'Failed to read profile JSON');
      setProfileData(null);
    } finally {
      setIsLoadingFile(false);
    }
  };

  const handleImport = async () => {
    if (!profileData) return;

    setIsImporting(true);
    setError(null);

    try {
      const result = await importProfile(profileData, (prog) => {
        setProgress(prog);
      });

      toast({
        title: 'Profile Imported Successfully',
        description: `Imported ${result.foldersCount} folders, ${result.listsCount} lists, ${result.sectionsCount} sections, and ${result.itemsCount} items.`,
      });

      await refreshData();
      handleDialogChange(false);
    } catch (err: any) {
      setError(err.message || 'Failed to import profile data');
      toast({
        variant: 'destructive',
        title: 'Import Failed',
        description: err.message || 'An error occurred while importing the profile.',
      });
    } finally {
      setIsImporting(false);
    }
  };

  const calculateProgressPercent = () => {
    if (!progress || progress.total === 0) return 0;
    return Math.round((progress.current / progress.total) * 100);
  };

  return (
    <Dialog open={open} onOpenChange={handleDialogChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="w-5 h-5 text-blue-600" />
            Import Profile
          </DialogTitle>
          <DialogDescription>
            Select a Listify profile JSON file to import folders, lists, sections, and items into your account.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-3">
          {/* File Picker */}
          <div
            onClick={() => !isImporting && fileInputRef.current?.click()}
            className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors ${
              isImporting
                ? 'opacity-50 cursor-not-allowed bg-slate-50 border-slate-200'
                : 'border-slate-300 hover:border-blue-500 hover:bg-blue-50/50'
            }`}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".json,application/json"
              className="hidden"
              onChange={handleFileChange}
              disabled={isImporting}
            />
            <div className="flex flex-col items-center justify-center gap-2">
              <FileText className="w-8 h-8 text-slate-400" />
              <div className="text-sm font-medium text-slate-700">
                {selectedFile ? selectedFile.name : 'Click to select a JSON profile file'}
              </div>
              <p className="text-xs text-slate-500">Supported format: .json</p>
            </div>
          </div>

          {/* Loading File state */}
          {isLoadingFile && (
            <div className="flex items-center justify-center gap-2 text-sm text-slate-500 py-2">
              <Loader2 className="w-4 h-4 animate-spin text-blue-500" />
              Validating profile file...
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              <AlertCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
              <div className="flex-1">{error}</div>
            </div>
          )}

          {/* Profile Overview before import */}
          {profileData && !isLoadingFile && (
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 space-y-2 text-xs text-slate-700">
              <div className="font-semibold text-slate-900 flex items-center justify-between">
                <span>Profile Content Preview:</span>
                {profileData.exportedAt && (
                  <span className="font-normal text-slate-500">
                    Exported: {new Date(profileData.exportedAt).toLocaleDateString()}
                  </span>
                )}
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="bg-white p-2 rounded border border-slate-100 flex justify-between">
                  <span className="text-slate-500">Folders:</span>
                  <span className="font-semibold text-slate-900">{profileData.folders.length}</span>
                </div>
                <div className="bg-white p-2 rounded border border-slate-100 flex justify-between">
                  <span className="text-slate-500">Lists:</span>
                  <span className="font-semibold text-slate-900">{profileData.lists.length}</span>
                </div>
                <div className="bg-white p-2 rounded border border-slate-100 flex justify-between">
                  <span className="text-slate-500">Sections:</span>
                  <span className="font-semibold text-slate-900">{profileData.sections.length}</span>
                </div>
                <div className="bg-white p-2 rounded border border-slate-100 flex justify-between">
                  <span className="text-slate-500">Items:</span>
                  <span className="font-semibold text-slate-900">{profileData.listItems.length}</span>
                </div>
              </div>
            </div>
          )}

          {/* Import Progress */}
          {isImporting && (
            <div className="space-y-2 bg-blue-50 border border-blue-100 rounded-lg p-3">
              <div className="flex items-center justify-between text-xs font-medium text-blue-900">
                <span className="flex items-center gap-2">
                  <Loader2 className="w-3.5 h-3.5 animate-spin text-blue-600" />
                  {progress?.message || 'Processing import...'}
                </span>
                <span>{calculateProgressPercent()}%</span>
              </div>
              <div className="w-full bg-blue-200 rounded-full h-2 overflow-hidden">
                <div
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${calculateProgressPercent()}%` }}
                />
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            type="button"
            variant="outline"
            onClick={() => handleDialogChange(false)}
            disabled={isImporting}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleImport}
            disabled={!profileData || isImporting || isLoadingFile}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {isImporting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Importing...
              </>
            ) : (
              <>
                <Upload className="w-4 h-4 mr-2" />
                Start Import
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
